import { useDocumentReferencesForBinaryIds } from '@ctw/shared/api/fhir/binaries';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { ExportDocumentInfo } from '@ctw/shared/content/fact-extraction/compute-export-summary';
import { resolveFactDocumentsForExport } from '@ctw/shared/content/fact-extraction/export-citation-resolution';
import type { MappedFactGroup } from '@ctw/shared/content/fact-extraction/map-extraction-job';
import type { ResolvedCitation } from '@ctw/shared/content/fact-extraction/use-citation-sources';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useMemo } from 'react';

export function classifyExportDocument(
  binaryId: string,
  label: string,
  model: DocumentModel | undefined,
): ExportDocumentInfo {
  if (!model) {
    return {
      binaryId,
      title: label,
      status: 'unresolved',
      reason: 'No document record found for this source',
    };
  }
  const base = { binaryId, title: model.title, date: model.dateCreated };
  if (model.isPdf) {
    return { ...base, status: 'pdf', documentReferenceId: model.id };
  }
  if (model.isCDA) {
    return { ...base, status: 'ccda', documentReferenceId: model.id };
  }
  return {
    ...base,
    status: 'unsupported',
    reason: `Format not supported for PDF export (${model.contentType ?? 'unknown'})`,
  };
}

/**
 * Resolves every document-kind citation source to its DocumentReference (one
 * batched round, cached) and classifies deliverability. Runs when the export
 * modal opens so the modal's source counts reflect what the export can
 * actually deliver, and the download step reuses the cached resolution.
 */
export function useExportDocuments(
  groups: Array<MappedFactGroup>,
  clientResolvedByCitationId: ReadonlyMap<number, ResolvedCitation>,
): {
  exportDocumentsByBinaryId: ReadonlyMap<string, ExportDocumentInfo>;
  isLoading: boolean;
  // True when resolution FAILED (vs. resolved to nothing). Callers must not
  // treat the sources as merely unresolved — an export built in this state
  // would silently contain zero documents.
  isError: boolean;
} {
  const { patient } = usePatientContext();

  // binaryId -> label, deduped. Resolved per (value, citation) with the same
  // service-first precedence the summary/icon use, so the classified set
  // covers every document the export could reference — service-provided
  // binaries included, not just what client-side resolution found.
  const labelsByBinaryId = useMemo(() => {
    const labels = new Map<string, string>();
    for (const group of groups) {
      for (const fact of group.facts) {
        for (const document of resolveFactDocumentsForExport({
          citationIds: fact.citationIds,
          serviceSourceDocuments: fact.serviceSourceDocuments,
          clientResolvedByCitationId,
        })) {
          if (!labels.has(document.binaryId)) {
            labels.set(document.binaryId, document.label);
          }
        }
      }
    }
    return labels;
  }, [groups, clientResolvedByCitationId]);

  const binaryIds = [...labelsByBinaryId.keys()];

  const query = useDocumentReferencesForBinaryIds({
    upid: patient.UPID ?? '',
    binaryIds,
    enabled: binaryIds.length > 0,
  });

  const exportDocumentsByBinaryId = useMemo(() => {
    const modelsByBinaryId = new Map<string, DocumentModel>();
    for (const model of query.data ?? []) {
      if (model.binaryId) {
        modelsByBinaryId.set(model.binaryId, model);
      }
    }
    return new Map(
      [...labelsByBinaryId].map(([binaryId, label]) => [
        binaryId,
        classifyExportDocument(binaryId, label, modelsByBinaryId.get(binaryId)),
      ]),
    );
  }, [labelsByBinaryId, query.data]);

  return {
    exportDocumentsByBinaryId,
    isLoading: binaryIds.length > 0 && query.isLoading,
    isError: binaryIds.length > 0 && query.isError,
  };
}
